import { useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { ColumnsType } from 'antd/lib/table'
import { CheckboxOptionType, Space, TablePaginationConfig, Tooltip } from 'antd'
import { ButtonIcon, Dot, EmptySpaceIcon } from './styles'
import { UserSelectStyle } from '@/components/common/Table/styles'
import { IMAGE_URL } from '@/constant/IMAGE'
import CONSTANT from '@/constant'
import { useClaimList } from '@/services/healthClaim/useClaimList'
import { useSarabanList } from '@/services/healthClaim/useSaraban'
import {
  CLAIM_STATUS_DISPLAY,
  ClaimFilterTypeEnum,
  ClaimStatusCodeEnum,
  RECEIVED_DOCUMENT_STATUS_DISPLAY,
  ReceivedDocumentStatusEnum,
  REQUEST_DOCUMENT_STATUS_DISPLAY,
  RequestDocumentStatusEnum,
} from './types'
import { SorterEnum } from '@/constant/USER'
import { toThaiLocaleDateDash, toThaiLocaleDateDashAndTime } from '@/libs/date'
import userAtom from '@/recoil/user'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { validatePerm } from '@/libs/validatePermission'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'
import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs'
import { SorterResult } from 'antd/es/table/interface'
import { FilterValue, SortOrder } from 'antd/lib/table/interface'
import { useFilterOptionEClaimStatus } from '@/services/healthClaim/useFilterOptionEClaimStatus'
import { useFilterOptionTreatmentType } from '@/services/healthClaim/useFilterOptionTreatmentType'
import { useFilterOptionExaminer } from '@/services/healthClaim/useFilterOptionExaminer'
import { useFilterOptionPolicyGroup } from '@/services/healthClaim/useFilterOptionPolicyGroup'
import { fetchRejectDocument } from '@/services/healthClaim/useRejectDocument'
import { useCancelCeClaim } from '@/services/healthClaim/useCancelCeClaim'
import message from '@/libs/message'
import copyToClipboard from '@/libs/utils/copy-to clipboard.util'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import healthClaimFormAtom from '@/recoil/healthClaimForm'
import { useListHealthClaimUserApprove } from '@/services/healthClaim/useListHealthClaimUserApprove'
import ClaimActionBarHook from '@/components/feature/HealthClaimDetailPage/ClaimActionBar/ClaimActionBar.hook'
import { useFilterOptionEClaimSarabanStatus } from '@/services/healthClaim/useFilterOptionEClaimSarabanStatus'

const inputChange = new BehaviorSubject('')
const inputChange$ = inputChange.asObservable()

const ClaimHandlingTableHook = () => {
  const user = useRecoilValue(userAtom)
  const { isHealthClaimApprover } = ClaimActionBarHook()
  const hasClaimHandlingPermission = validatePerm(
    [BACKOFFICE_USER_PERMISSION.CLM010101],
    user.permissions,
  )
  const hasCorrespondencePermission = validatePerm(
    [BACKOFFICE_USER_PERMISSION.CLM010102],
    user.permissions,
  )
  const radioOptions: Array<CheckboxOptionType> = [
    {
      label: 'งานสินไหม',
      value: ClaimFilterTypeEnum.CLAIM_HANDLING,
      disabled: !hasClaimHandlingPermission, // Disable if user has no permission
    },
    {
      label: 'งานสารบรรณ',
      value: ClaimFilterTypeEnum.CORRESPONDENCE,
      disabled: !hasCorrespondencePermission, // Disable if user has no permission
    },
  ]
  const navigate = useNavigate()
  const setHealthClaim = useSetRecoilState(healthClaimFormAtom)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [selectClaimFormId, setSelectClaimFormId] = useState<string>('')
  const [selectInsuredName, setSelectInsuredName] = useState<string>('')
  const [selectClaimNumber, setSelectClaimNumber] = useState<string>('')
  const [isCancelClaimModalOpen, setIsCancelClaimModalOpen] = useState<boolean>(false)
  const [cancelClaimFormId, setCancelClaimFormId] = useState<string>('')
  const [cancelClaimNumber, setCancelClaimNumber] = useState<string>('')
  const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState<boolean>(false)
  const [changeOwnerClaimFormId, setChangeOwnerClaimFormId] = useState<string>('')
  const [changeOwnerCurrentOwnerId, setChangeOwnerCurrentOwnerId] = useState<string | null>(null)
  const [selectTableType, setSelectTableType] = useState<
    ClaimFilterTypeEnum.CLAIM_HANDLING | ClaimFilterTypeEnum.CORRESPONDENCE
  >(ClaimFilterTypeEnum.CLAIM_HANDLING)
  const [inputValue, setInputValue] = useState<string>('')
  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setInputValue(value) // Update input immediately for smooth typing
    inputChange.next(value)
  }
  const [search, setSearch] = useState<string>('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [sortBy, setSortBy] = useState('')
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>({})
  const [selectedRowKey, setSelectedRowKey] = useState<string>('')
  const [formattedFilters, setFormattedFilters] = useState<string>('')
  const queryFilterOptionEClaimStatus = useFilterOptionEClaimStatus()
  const queryFilterOptionEClaimSarabanStatus = useFilterOptionEClaimSarabanStatus()
  const queryFilterOptionPolicyGroup = useFilterOptionPolicyGroup()
  const queryFilterOptionTreatmentType = useFilterOptionTreatmentType()
  const queryFilterOptionExaminer = useFilterOptionExaminer()
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const querySaraban = useSarabanList({
    searchDetails: search,
    page: page || 1,
    limit: limit || 10,
    sortBy,
    filter: formattedFilters,
    enabled: selectTableType === ClaimFilterTypeEnum.CORRESPONDENCE,
  })
  const queryClaim = useClaimList({
    searchDetails: search,
    page: page || 1,
    limit: limit || 10,
    sortBy,
    filter: formattedFilters,
    enabled: selectTableType === ClaimFilterTypeEnum.CLAIM_HANDLING,
  })
  const backofficeFullname = `${user.firstname} ${user.lastname}`
  const queryApprover = useListHealthClaimUserApprove({
    search: backofficeFullname,
    page: 1,
    limit: 1,
    enabled: !!backofficeFullname,
  })
  const { mutateAsync: cancelCeClaim, isPending: isCancelClaimPending } = useCancelCeClaim()

  const isTableLoading = querySaraban.isLoading || queryClaim.isLoading

  const handleOpenCancelClaimModal = (claimFormId: string, claimNumber: string) => {
    setCancelClaimFormId(claimFormId)
    setCancelClaimNumber(claimNumber)
    setIsCancelClaimModalOpen(true)
  }

  const handleCloseCancelClaimModal = () => {
    setIsCancelClaimModalOpen(false)
  }

  const handleConfirmCancelClaim = async () => {
    if (!cancelClaimFormId) return

    try {
      await cancelCeClaim(cancelClaimFormId)

      await (selectTableType === ClaimFilterTypeEnum.CLAIM_HANDLING
        ? queryClaim.refetch()
        : querySaraban.refetch())

      message.success({
        content: 'ยกเลิกเคสเคลมสำเร็จ',
        duration: 10,
      })
    } catch (error) {
      message.error({
        content: 'ยกเลิกเคสเคลมไม่สำเร็จ',
        duration: 10,
      })
    } finally {
      setIsCancelClaimModalOpen(false)
      setCancelClaimFormId('')
      setCancelClaimNumber('')
    }
  }

  const redirectToCreateClaimDetailPage = () => {
    // Ensure fresh create session: clear any reclaim/draft/data residue
    setHealthClaim(
      (prev) =>
        ({
          mode: HealthClaimModeEnum.CREATE,
          data: undefined,
          reclaim: false,
          hasRequestedDocuments: false,
          pendingRequestDocuments: [],
          draft: undefined as any,
        } as any),
    )
    navigate(generatePath(CONSTANT.ROUTES.HEALTH_CLAIM_CREATE_PAGE), {
      state: { referrer: CONSTANT.ROUTES.HEALTH_CLAIM_PAGE },
    })
  }

  const redirectToClaimDetailPage = (claimFormId: string, mode: HealthClaimModeEnum) => {
    const searchParams = new URLSearchParams()
    const route = {
      [HealthClaimModeEnum.VIEW]: CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE,
      [HealthClaimModeEnum.EDIT]: CONSTANT.ROUTES.HEALTH_CLAIM_EDIT_PAGE,
      [HealthClaimModeEnum.CREATE]: CONSTANT.ROUTES.HEALTH_CLAIM_CREATE_PAGE,
    }

    navigate(
      {
        pathname: generatePath(route[mode], { claimFormId }),
        search: searchParams.toString(),
      },
      {
        state: { referrer: CONSTANT.ROUTES.HEALTH_CLAIM_PAGE, claimFormId },
      },
    )
  }

  const getClaimChannelLabel = (claimChannel: string) => {
    const label = {
      BACKOFFICE: 'พนักงานสินไหม',
      SUPERAPP: 'Thaivivat App',
    }

    return label[claimChannel as keyof typeof label] || claimChannel || '-'
  }

  const getStatusFilterOptions = () => {
    let statusList = []

    if (selectTableType === ClaimFilterTypeEnum.CLAIM_HANDLING) {
      statusList = queryFilterOptionEClaimStatus.data
    } else {
      statusList = queryFilterOptionEClaimSarabanStatus.data
    }

    return (
      statusList?.map((item) => ({
        text: CLAIM_STATUS_DISPLAY[item.value as ClaimStatusCodeEnum],
        value: item.value,
      })) || []
    )
  }

  const columns: ColumnsType<any> = [
    {
      title: 'ลำดับ',
      dataIndex: 'index',
      key: 'index',
      className: UserSelectStyle,
      render: (_text, _record, index) => (page - 1) * limit + index + 1,
    },
    {
      title: 'เลขรับแจ้ง',
      dataIndex: 'notifyNumber',
      key: 'notifyNumber',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'เลขเคลม',
      dataIndex: 'claimNumber',
      key: 'claimNumber',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'เลขที่ใบสั่งจ่าย',
      dataIndex: 'pvClaimPaymentNumber',
      key: 'pvClaimPaymentNumber',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'เลขที่กรมธรรม์',
      dataIndex: 'policyNumber',
      key: 'policyNumber',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'ประเภทกรมธรรม์',
      dataIndex: 'policyGroup',
      key: 'policyGroup',
      className: UserSelectStyle,
      filters:
        queryFilterOptionPolicyGroup.data?.map((item) => ({
          text: item.display,
          value: item.value,
        })) || [],
      filteredValue: filters['policyGroup'] || null,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'วันที่แจ้ง',
      dataIndex: 'notifyAt',
      key: 'notifyAt',
      className: UserSelectStyle,
      // sorter: true,
      // sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      render: (notifyAt: string) => {
        return notifyAt ? toThaiLocaleDateDashAndTime(notifyAt) : '-'
      },
    },
    {
      title: 'ประเภทการรักษา',
      dataIndex: 'treatmentType',
      key: 'treatmentType',
      className: UserSelectStyle,
      filters:
        queryFilterOptionTreatmentType.data?.map((item) => ({
          text: item.display,
          value: item.value,
        })) || [],
      filteredValue: filters['treatmentType'] || null,
    },
    {
      // correspondence will hide this column
      title: 'สถานะ',
      dataIndex: 'claimStatusCode',
      key: 'claimStatus',
      className: UserSelectStyle,
      filters: getStatusFilterOptions(),
      filteredValue: filters['claimStatus'] || null,
      render: (claimStatusCode: ClaimStatusCodeEnum) => {
        return (
          <Space>
            <Dot status={claimStatusCode} />
            {CLAIM_STATUS_DISPLAY[claimStatusCode]}
          </Space>
        )
      },
    },
    {
      title: 'ชื่อผู้เอาประกัน',
      dataIndex: 'insuredName',
      key: 'insuredName',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will move this column after bankAccountName
      title: 'เบอร์โทรศัพท์',
      dataIndex: 'mobilePhone',
      key: 'mobilePhone',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'เลขบัตรประชาชน',
      dataIndex: 'identityId',
      key: 'identityId',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'สถานพยาบาล',
      dataIndex: 'hospitalName',
      key: 'hospitalName',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'วันที่เข้ารับการรักษา',
      dataIndex: 'treatmentDateIn',
      key: 'treatmentDateIn',
      className: UserSelectStyle,
      render: (treatmentDateIn: string) => {
        return treatmentDateIn ? toThaiLocaleDateDash(treatmentDateIn) : '-'
      },
    },
    {
      // correspondence will use this column
      title: 'วันที่ออกจากโรงพยาบาล',
      dataIndex: 'treatmentDateOut',
      key: 'treatmentDateOut',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'ค่ารักษาพยาบาลตามใบเสร็จ',
      dataIndex: 'treatmentAmount',
      key: 'treatmentAmount',
      className: UserSelectStyle,
      render: (treatmentAmount: number) => {
        return treatmentAmount ? treatmentAmount.toLocaleString('th-TH') : '-'
      },
    },
    {
      // correspondence will use this column
      title: 'เลขที่ใบเสร็จ',
      dataIndex: 'receiptNumber',
      key: 'receiptNumber',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      title: 'ค่าสินไหมที่อนุมัติ',
      dataIndex: 'approvedClaimAmount',
      key: 'approvedClaimAmount',
      className: UserSelectStyle,
      render: (approvedClaimAmount: number) => {
        return approvedClaimAmount ? approvedClaimAmount.toLocaleString('th-TH') : '-'
      },
    },
    {
      title: 'ช่องทางการทำรายการ',
      dataIndex: 'claimChannel',
      key: 'claimChannel',
      className: UserSelectStyle,
      render: (value) => {
        return getClaimChannelLabel(value)
      },
    },
    {
      // correspondence will use this column
      title: 'ชื่อโรค',
      dataIndex: 'diseasesName',
      key: 'diseasesName',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'สถานที่เกิดเหตุ',
      dataIndex: 'accidentLocation',
      key: 'accidentLocation',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'วันที่เกิดเหตุ',
      dataIndex: 'accidentDate',
      key: 'accidentDate',
      className: UserSelectStyle,
      sorter: true,
      sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      render: (accidentDate: string) => {
        return accidentDate ? toThaiLocaleDateDash(accidentDate) : '-'
      },
    },
    {
      // correspondence will use this column
      title: 'เวลาเกิดเหตุ',
      dataIndex: 'accidentTime',
      key: 'accidentTime',
      className: UserSelectStyle,
      sorter: true,
      sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      render: (accidentTime: string) => {
        return accidentTime ? accidentTime : '-'
      },
    },
    {
      // correspondence will use this column
      title: 'ลักษณะการเกิดเหตุ',
      dataIndex: 'accidentDetails',
      key: 'accidentDetails',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'ตรวจสอบข้อมูลโดย',
      key: 'ownerId', // for filtering
      className: UserSelectStyle,
      filters:
        queryFilterOptionExaminer.data?.map((item) => ({
          text: item.display,
          value: item.value,
        })) || [],
      filteredValue: filters['ownerId'] || null,
      render: (_value, record) => {
        const canChangeOwner = validatePerm(
          [BACKOFFICE_USER_PERMISSION.CLM010107],
          user.permissions,
        )
        const allowedStatuses = [
          ClaimStatusCodeEnum.IN_REVIEW,
          ClaimStatusCodeEnum.PENDING_APPROVAL,
          ClaimStatusCodeEnum.PENDING_PAYMENT,
          ClaimStatusCodeEnum.DOCUMENTS_REQUESTED,
          ClaimStatusCodeEnum.APPROVED,
        ]
        const canShowChangeOwner =
          canChangeOwner && allowedStatuses.includes(record.claimStatusCode)

        const handleOpenChangeOwnerModal = () => {
          setChangeOwnerClaimFormId(record.claimFormId)
          setChangeOwnerCurrentOwnerId(record.ownerId)
          setIsChangeOwnerModalOpen(true)
        }

        return (
          <Space>
            <span>{record.ownerName || record.autoAssignUserName || '-'}</span>
            {canShowChangeOwner ? (
              <Tooltip title="เปลี่ยนผู้ดำเนินการ">
                <ButtonIcon
                  src={IMAGE_URL.editIcon}
                  style={{ width: 16, height: 16 }}
                  onClick={handleOpenChangeOwnerModal}
                />
              </Tooltip>
            ) : null}
          </Space>
        )
      },
    },
    {
      // correspondence will use this column
      title: 'ช่องทางการรับสินไหมทดแทน',
      dataIndex: 'reimbursementMethod',
      key: 'reimbursementMethod',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'ชื่อธนาคาร',
      dataIndex: 'bankName',
      key: 'bankName',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'ชื่อสาขา',
      dataIndex: 'bankBranch',
      key: 'bankBranch',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'เลขที่บัญชี',
      dataIndex: 'bankAccountNumber',
      key: 'bankAccountNumber',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'ชื่อบัญชี',
      dataIndex: 'bankAccountName',
      key: 'bankAccountName',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'ที่อยู่รับทางไปรษณีย์',
      dataIndex: 'postAddress',
      key: 'postAddress',
      className: UserSelectStyle,
      render: (value) => {
        return value ? (
          <Tooltip title="คลิกเพื่อคัดลอก">
            <div onClick={() => copyToClipboard(value)}>{value}</div>
          </Tooltip>
        ) : (
          '-'
        )
      },
    },
    {
      title: 'สถานะรับเอกสารเพิ่มเติม',
      dataIndex: 'requestDocumentStatus',
      key: 'requestDocumentStatus',
      className: UserSelectStyle,
      filters: Object.entries(REQUEST_DOCUMENT_STATUS_DISPLAY).map(([value, text]) => ({
        text,
        value,
      })),
      filteredValue: filters['requestDocumentStatus'] || null,
      render: (requestDocumentStatus: RequestDocumentStatusEnum) => {
        return (
          <Space>
            <Dot status={requestDocumentStatus} />
            {REQUEST_DOCUMENT_STATUS_DISPLAY[requestDocumentStatus]}
          </Space>
        )
      },
    },
    {
      title: 'สถานะรับเอกสารจริง',
      dataIndex: 'receivedDocumentStatus',
      key: 'receivedDocumentStatus',
      className: UserSelectStyle,
      filters: Object.entries(RECEIVED_DOCUMENT_STATUS_DISPLAY).map(([value, text]) => ({
        text,
        value,
      })),
      filteredValue: filters['receivedDocumentStatus'] || null,
      render: (receivedDocumentStatus: ReceivedDocumentStatusEnum) => {
        return (
          <Space>
            <Dot status={receivedDocumentStatus} />
            {RECEIVED_DOCUMENT_STATUS_DISPLAY[receivedDocumentStatus]}
          </Space>
        )
      },
    },
    {
      title: 'วันที่อนุมัติ',
      dataIndex: 'approveAt',
      key: 'approveAt',
      className: UserSelectStyle,
      render: (approveAt: string) => {
        return approveAt ? toThaiLocaleDateDashAndTime(approveAt) : '-'
      },
    },
    {
      title: 'อนุมัติโดย',
      dataIndex: 'approvedBy',
      key: 'approvedBy',
      className: UserSelectStyle,
      render: (approvedBy: string) => {
        return approvedBy || '-'
      },
    },
    {
      // correspondence will use this column
      title: 'หมายเหตุ',
      dataIndex: 'remark',
      key: 'remark',
      className: UserSelectStyle,
      render: (value) => {
        return value || '-'
      },
    },
    {
      // correspondence will hide this column
      title: 'อัปเดตล่าสุด',
      dataIndex: 'lastUpdatedAt',
      key: 'lastUpdatedAt',
      className: UserSelectStyle,
      render: (lastUpdatedAt: string) => {
        return lastUpdatedAt ? toThaiLocaleDateDashAndTime(lastUpdatedAt) : '-'
      },
    },
    {
      title: '',
      fixed: 'right',
      render: (_value, record) => {
        const isTheSameUser = record.ownerId === user.id
        const canViewDetail = validatePerm([BACKOFFICE_USER_PERMISSION.CLM010104], user.permissions)
        const canEdit = validatePerm([BACKOFFICE_USER_PERMISSION.CLM010105], user.permissions)
        const isManager = user.permissions.includes(BACKOFFICE_USER_PERMISSION.APP010104)
        const canViewDoc = record.hasRejectDocument
        const approver = queryApprover.data?.data[0]
        const claimStatusCanCancelCe = [
          ClaimStatusCodeEnum.SUBMITTED,
          ClaimStatusCodeEnum.IN_REVIEW,
          ClaimStatusCodeEnum.PENDING_APPROVAL,
          ClaimStatusCodeEnum.APPROVED,
        ]
        const hasCancelCePermission = validatePerm(
          [BACKOFFICE_USER_PERMISSION.CLM010108],
          user.permissions,
        )
        const canCancelCeByStatus = claimStatusCanCancelCe.includes(record.claimStatusCode)
        const canApprove = isHealthClaimApprover(approver, record.approvedClaimAmount)
        const canShowEditButton = () => {
          const restrictedStatuses = [
            ClaimStatusCodeEnum.APPROVED,
            ClaimStatusCodeEnum.PENDING_PAYMENT,
            ClaimStatusCodeEnum.COMPLETED,
            ClaimStatusCodeEnum.PENDING_DOCUMENT,
            ClaimStatusCodeEnum.REJECTED,
            ClaimStatusCodeEnum.REJECTED_ON_PV,
          ]

          if (restrictedStatuses.includes(record.claimStatusCode)) {
            return false
          }

          if (record.claimStatusCode === ClaimStatusCodeEnum.PENDING_APPROVAL) {
            if (!approver || isTheSameUser) {
              return false
            }

            return isManager && canApprove
          }

          return canEdit && (isTheSameUser || !record.ownerId)
        }

        const handleClickEditButton = () => {
          setSelectClaimFormId(record.claimFormId)
          setSelectInsuredName(record.insuredName)
          setSelectClaimNumber(record.claimNumber)

          if (
            isManager &&
            canApprove &&
            !isTheSameUser &&
            record.claimStatusCode === ClaimStatusCodeEnum.PENDING_APPROVAL
          ) {
            // Managers who can approve should not be blocked by owner check
            redirectToClaimDetailPage(record.claimFormId, HealthClaimModeEnum.EDIT)
            return
          }

          if (!isTheSameUser) {
            setIsModalOpen(true)
          } else {
            redirectToClaimDetailPage(record.claimFormId, HealthClaimModeEnum.EDIT)
          }
        }

        return (
          <Space size="middle">
            {canViewDoc ? (
              <Tooltip title="ดูเอกสารปฎิเสธคำขอ">
                <ButtonIcon
                  src={IMAGE_URL.documentIcon}
                  onClick={() => {
                    fetchRejectDocument(record.claimFormId)
                      .then((documents) => {
                        if (typeof documents === 'string' && documents.startsWith('http')) {
                          return setDocumentUrl(documents)
                        }
                      })
                      .catch((error) => {
                        message.error({
                          content: 'error',
                          duration: 10,
                        })
                      })
                  }}
                />
              </Tooltip>
            ) : canShowEditButton() ? (
              <Tooltip title="แก้ไข">
                <ButtonIcon src={IMAGE_URL.editIcon} onClick={() => handleClickEditButton()} />
              </Tooltip>
            ) : (
              // if no edit permission, show empty space
              <EmptySpaceIcon />
            )}
            {canViewDetail ? (
              <Tooltip title="ดูรายละเอียด">
                <ButtonIcon
                  src={IMAGE_URL.eyeIcon}
                  onClick={() =>
                    redirectToClaimDetailPage(record.claimFormId, HealthClaimModeEnum.VIEW)
                  }
                />
              </Tooltip>
            ) : (
              // if no view detail permission, show empty space
              <EmptySpaceIcon />
            )}
            {hasCancelCePermission ? (
              canCancelCeByStatus ? (
                <Tooltip title="ยกเลิกเคสเคลม">
                  <ButtonIcon
                    src={IMAGE_URL.closeIcon}
                    onClick={() =>
                      handleOpenCancelClaimModal(record.claimFormId, record.claimNumber)
                    }
                  />
                </Tooltip>
              ) : (
                <EmptySpaceIcon />
              )
            ) : undefined}
          </Space>
        )
      },
    },
  ]

  const getColumnsByType = (
    type: ClaimFilterTypeEnum.CLAIM_HANDLING | ClaimFilterTypeEnum.CORRESPONDENCE,
  ): ColumnsType<any> => {
    const isCorrespondence = type === ClaimFilterTypeEnum.CORRESPONDENCE

    const filteredColumns = columns.filter((column) => {
      const key = column.key as string

      const hiddenForCorrespondence = ['claimChannel', 'lastUpdatedAt', 'claimStatus']

      const usedOnlyInCorrespondence = [
        'policyGroup',
        'treatmentDateOut',
        'receiptNumber',
        'diseasesName',
        'accidentLocation',
        'accidentDate',
        'accidentTime',
        'accidentDetails',
        'claimHandler',
        'reimbursementMethod',
        'bankName',
        'bankBranch',
        'bankAccountNumber',
        'bankAccountName',
        'postAddress',
        'remark',
        'pvClaimPaymentNumber',
      ]

      // For ClaimFilterTypeEnum.CLAIM_HANDLING, exclude correspondence-specific columns
      if (!isCorrespondence && usedOnlyInCorrespondence.includes(key)) {
        return false
      }

      // For ClaimFilterTypeEnum.CORRESPONDENCE, exclude columns marked to be hidden
      if (isCorrespondence && hiddenForCorrespondence.includes(key)) {
        return false
      }

      return true
    })

    return filteredColumns
  }

  const getDataByType = (
    type: ClaimFilterTypeEnum.CLAIM_HANDLING | ClaimFilterTypeEnum.CORRESPONDENCE,
  ) => {
    if (type === ClaimFilterTypeEnum.CLAIM_HANDLING) {
      return queryClaim
    } else {
      return querySaraban
    }
  }

  const handleSortBy = (field?: React.Key | readonly React.Key[], order?: SortOrder | '') => {
    const filterPrefix = 'hcDetail.'
    const sortableWithPrefixFields = ['treatmentDateIn', 'treatmentDateOut', 'accidentDate']

    if (field && sortableWithPrefixFields.includes(field as string)) {
      field = `${filterPrefix}${field}`
    }

    switch (order) {
      case SorterEnum.ASC:
        return `${field}:ASC`
      case SorterEnum.DESC:
        return `${field}:DESC`
      default:
        return ''
    }
  }

  const handleFilter = (filters: Record<string, FilterValue | null>) => {
    interface FilterMapping {
      key: string
      prefix: string
    }

    const filterAndPrefixList: FilterMapping[] = [
      {
        key: 'claimStatus',
        prefix: 'filter.currentClaimStatus.code',
      },
      {
        key: 'ownerId',
        prefix: 'filter.examiner',
      },
      {
        key: 'treatmentType',
        prefix: 'filter.hcDetail.treatmentType.code',
      },
      {
        key: 'policyGroup',
        prefix: 'filter.policyGroup',
      },
      {
        key: 'requestDocumentStatus',
        prefix: 'filter.requestDocumentStatus',
      },
      {
        key: 'receivedDocumentStatus',
        prefix: 'filter.receivedDocumentStatus',
      },
    ]

    const formattedFilters = Object.entries(filters)
      .map(([key, value]) => {
        const filterItem = filterAndPrefixList.find((item) => item.key === key)
        if (filterItem && value) {
          const prefix = filterItem.prefix
          if (Array.isArray(value)) {
            return value
              .map((v, i) => {
                if (i === 0) {
                  return `${prefix}=$in:${v}`
                }
                return v
              })
              .join(',')
          } else {
            return `${prefix}=$in:${value}`
          }
        }
        return ''
      })
      .filter(Boolean)
      .join('&')

    setFormattedFilters(formattedFilters) // Update state with formatted filters
  }

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<{}> | SorterResult<{}>[],
  ) => {
    const { current, pageSize } = pagination
    const sort = Array.isArray(sorter) ? sorter[0] : sorter
    const { field, order } = sort || { field: '', order: '' }
    const sortBy = handleSortBy(field, order)

    handleFilter(filters) // Update formatted filters based on current filters
    setPage(current || 1)
    setLimit(pageSize || 10)
    setSortBy(sortBy)
    setFilters(filters)
  }

  useEffect(() => {
    // Determine default table type based on user permissions
    // Priority: claim handling > correspondence
    if (hasClaimHandlingPermission) {
      setSelectTableType(ClaimFilterTypeEnum.CLAIM_HANDLING)
    } else if (hasCorrespondencePermission) {
      setSelectTableType(ClaimFilterTypeEnum.CORRESPONDENCE)
    }
    // If user has no permissions for either, keep current state
  }, [hasClaimHandlingPermission, hasCorrespondencePermission])

  const onChangeTableType = (tableType: ClaimFilterTypeEnum) => {
    setSelectTableType(tableType)
    setPage(1) // Reset to first page when changing table type
    setSearch('') // Reset search input when changing table type
    setInputValue('') // Reset input value immediately
    inputChange.next('') // Reset BehaviorSubject value
    setFormattedFilters('') // Reset formatted filters
    setSortBy('') // Reset sort by
    setFilters({}) // Reset filters
  }

  useEffect(() => {
    const subscription = inputChange$
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((value) => {
        setSearch(value)
        setPage(1) // Reset to first page when search input changes
      })

    return () => {
      // Reset the BehaviorSubject value when the component unmounts
      inputChange.next('')
      return subscription.unsubscribe()
    }
  }, [])

  return {
    columns,
    isModalOpen,
    isCancelClaimModalOpen,
    cancelClaimNumber,
    isCancelClaimPending,
    selectClaimFormId,
    selectClaimNumber,
    selectInsuredName,
    getColumnsByType,
    radioOptions,
    selectTableType,
    getDataByType,
    isTableLoading,
    user,
    inputValue,
    onSearch,
    onTableChange,
    onChangeTableType,
    redirectToCreateClaimDetailPage,
    documentUrl,
    setDocumentUrl,
    handleCloseCancelClaimModal,
    handleConfirmCancelClaim,
    selectedRowKey,
    setSelectedRowKey,
    isChangeOwnerModalOpen,
    setIsChangeOwnerModalOpen,
    changeOwnerClaimFormId,
    changeOwnerCurrentOwnerId,
  }
}

export default ClaimHandlingTableHook
